import { useState, FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

export default function ConnexionPage() {
  const navigate = useNavigate()
  const [showPwd, setShowPwd] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    setError('')

    const fd = new FormData(e.currentTarget)
    const email = fd.get('email') as string
    const password = fd.get('password') as string

    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.message || 'Identifiants invalides')
      }

      const data = await res.json()
      const { accessToken, role, companyType } = data
      if (!accessToken) throw new Error('Réponse serveur invalide')
      localStorage.setItem('velink_token', accessToken)
      // Reprise d'un parcours (ex. proposition de mission à un expert) — chemin relatif uniquement
      const next = new URLSearchParams(window.location.search).get('next') ?? ''
      if (next.startsWith('/') && !next.startsWith('//') && (role === 'COMPANY' || next === '/experts')) return navigate(next)
      if (role === 'ADMIN') navigate('/admin')
      else if (role === 'COMPANY' && companyType === 'TRAINING_CENTER') navigate('/cf-dashboard')
      else if (role === 'COMPANY') navigate('/dashboard')
      else navigate('/missions-disponibles')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : ''
      setError(msg || 'Erreur de connexion. Vérifiez votre connexion internet.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* Logo */}
      <div className="flex justify-center mb-8">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-accent/20 border border-accent/30 flex items-center justify-center">
            <span className="text-accent font-bold text-lg">V</span>
          </div>
          <span className="font-semibold text-text-primary text-xl tracking-tight">Velink</span>
        </div>
      </div>

      {/* CTAs inscription */}
      <div className="mb-6">
        <p className="text-center text-text-secondary text-sm mb-3">Nouveau sur Velink ?</p>
        <div className="flex flex-col gap-2">
          <Link
            to="/inscription/entreprise"
            className="glass-btn-accent py-3.5 text-sm rounded-xl text-center font-medium"
          >
            Je suis une entreprise →
          </Link>
          <div className="flex gap-2">
            <Link
              to="/inscription/centre-de-formation"
              className="flex-1 glass-btn py-3.5 text-sm rounded-xl text-center font-medium"
            >
              Je suis un centre de formation →
            </Link>
            <Link
              to="/inscription/prestataire"
              className="flex-1 glass-btn py-3.5 text-sm rounded-xl text-center font-medium"
            >
              Je suis prestataire →
            </Link>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-text-muted text-xs">Déjà un compte</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <div className="glass-card p-8">
        <div className="mb-7">
          <h1 className="text-2xl font-bold text-text-primary">Connexion</h1>
        </div>

        {error && (
          <div className="mb-5 px-4 py-3 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          <div>
            <label htmlFor="email" className="glass-label">Adresse email</label>
            <input
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              required
              placeholder="vous@exemple.fr"
              className="glass-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="password" className="glass-label mb-0">Mot de passe</label>
              <Link
                to="/mot-de-passe-oublie"
                className="text-xs text-text-muted hover:text-text-secondary transition-colors"
              >
                Mot de passe oublié ?
              </Link>
            </div>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                id="password"
                name="password"
                autoComplete="current-password"
                required
                placeholder="••••••••"
                className="glass-input pr-11"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors p-1"
                aria-label={showPwd ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPwd ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="glass-btn-accent w-full py-3 text-sm rounded-xl mt-2"
          >
            {loading ? 'Connexion en cours…' : 'Se connecter'}
          </button>
        </form>
      </div>
    </>
  )
}
